import { ItemPricing } from '../types';

/**
 * Format pricing information for display
 * @param pricing - The pricing object
 * @returns Formatted price string
 */
export const formatPricing = (pricing: ItemPricing): string => {
  if (pricing.description) {
    return pricing.description;
  }

  switch (pricing.model) {
    case 'per_item':
      return `$${pricing.price.toFixed(2)} per item`;
    case 'per_pound':
      return `$${pricing.price.toFixed(2)} per pound`;
    case 'per_hanging_weight':
      return `$${pricing.price.toFixed(2)} per pound (hanging weight)`;
    case 'fixed_price':
      return `$${pricing.price.toFixed(2)} flat fee`;
    default:
      return `$${pricing.price.toFixed(2)}`;
  }
};

/**
 * Calculate estimated pricing based on selection and weight
 * @param pricing - The pricing object
 * @param quantity - Number of items/pounds selected
 * @param hangingWeight - Total hanging weight (if applicable)
 * @returns Calculated price
 */
export const calculatePrice = (
  pricing: ItemPricing,
  quantity: number = 1,
  hangingWeight?: number,
): number => {
  switch (pricing.model) {
    case 'per_item':
      return pricing.price * quantity;
    case 'per_pound':
      return pricing.price * quantity;
    case 'per_hanging_weight':
      return hangingWeight ? pricing.price * hangingWeight : 0;
    case 'fixed_price':
      return pricing.price;
    default:
      return pricing.price;
  }
};

/**
 * Get the pricing model description for UI display
 * @param model - The pricing model
 * @returns User-friendly description
 */
export const getPricingModelLabel = (model: ItemPricing['model']): string => {
  switch (model) {
    case 'per_item':
      return 'Per Item';
    case 'per_pound':
      return 'Per Pound';
    case 'per_hanging_weight':
      return 'Per Pound of Hanging Weight';
    case 'fixed_price':
      return 'Fixed Fee';
    default:
      return 'Price';
  }
};
